import type { CartLine } from "@/shared/cart/model.types"
import type { BillingPreviewV1CartItem } from "@/domain/cart/billing-preview.types"
import { resolveModifierIdentity } from "@/domain/cart/cart.modifier-identity"
import { MAX_MENU_ITEM_QUANTITY, MAX_MODIFIER_QUANTITY } from "@/domain/cart/cart.constants"
import { isRfc4122Uuid } from "@/shared/ids/client-item-id"

function isLikelyClientId(id: string): boolean {
  // Wire + store must be Guid-compatible. Legacy cid- / :combo: ids are reminted on hydrate.
  return isRfc4122Uuid(id)
}

/**
 * Canonical aggregation: SUM(quantity) across every line sharing menu_item_id,
 * regardless of modifier/combo configuration. Single source of truth — reused by
 * the cart store, UI steppers, checkout validation, and reorder.
 */
export function sumMenuItemQuantity(lines: readonly { menu_item_id: string; quantity: number }[], menuItemId: string): number {
  const id = String(menuItemId ?? "").trim()
  if (!id) return 0
  let sum = 0
  for (const line of lines) {
    if (String(line?.menu_item_id) !== id) continue
    const qty = Number(line.quantity)
    if (Number.isFinite(qty) && qty > 0) sum += qty
  }
  return sum
}

export type MenuItemQuantityOverflow = { menu_item_id: string; total: number }

/** Every distinct menu_item_id whose aggregate quantity exceeds `max`, via {@link sumMenuItemQuantity}. */
export function findMenuItemQuantityOverflows(
  lines: readonly { menu_item_id: string; quantity: number }[],
  max: number
): MenuItemQuantityOverflow[] {
  const ids = new Set<string>()
  for (const line of lines) {
    const id = String(line?.menu_item_id ?? "").trim()
    if (id) ids.add(id)
  }
  const overflows: MenuItemQuantityOverflow[] = []
  for (const id of ids) {
    const total = sumMenuItemQuantity(lines, id)
    if (total > max) overflows.push({ menu_item_id: id, total })
  }
  return overflows
}

export type CartValidationIssue = {
  code: string
  message: string
  client_item_id?: string
  /** Set on aggregate menu-item-quantity issues, which span multiple lines (no single client_item_id). */
  menu_item_id?: string
}

/**
 * Validates raw cart lines before pricing transform (store shape).
 */
export function validateCartLines(lines: CartLine[]): CartValidationIssue[] {
  const issues: CartValidationIssue[] = []
  for (const line of lines) {
    const id = String(line?.client_item_id ?? "").trim()
    if (!id) {
      issues.push({ code: "MISSING_CLIENT_ID", message: "Cart line missing client_item_id" })
      continue
    }
    if (!isLikelyClientId(id)) {
      issues.push({
        code: "CLIENT_ID_FORMAT",
        message: "client_item_id is not a valid UUID or known synthetic id",
        client_item_id: id,
      })
    }
    const mid = String(line?.menu_item_id ?? "").trim()
    if (!mid) {
      issues.push({ code: "MISSING_MENU_ITEM", message: "Cart line missing menu_item_id", client_item_id: id })
    }
    const q = Math.floor(Number(line.quantity))
    if (!Number.isFinite(q) || q < 1) {
      issues.push({ code: "INVALID_QUANTITY", message: "Line quantity must be >= 1", client_item_id: id })
    }
    for (const g of line.selected_modifiers ?? []) {
      if (!String(g?.modifier_group_id ?? "").trim()) {
        issues.push({
          code: "EMPTY_MODIFIER_GROUP_ID",
          message: "Modifier group missing modifier_group_id",
          client_item_id: id,
        })
      }
      if (Array.isArray(g.items) && g.items.length === 0) {
        issues.push({
          code: "EMPTY_MODIFIER_GROUP",
          message: "Modifier group has no items",
          client_item_id: id,
        })
      }
      for (const m of g.items ?? []) {
        const ident = resolveModifierIdentity(m)
        if (!ident.primary_key) {
          issues.push({
            code: "MODIFIER_MISSING_IDENTITY",
            message: "Modifier row missing modifier_group_item_id and child_menu_item_id",
            client_item_id: id,
          })
        }
        if (ident.child_menu_item_id && !ident.modifier_group_item_id) {
          issues.push({
            code: "MODIFIER_BRIDGE_PREFERRED",
            message:
              "Modifier row should include modifier_group_item_id (bridge) when child_menu_item_id is set",
            client_item_id: id,
          })
        }
        if (m.quantity != null && m.quantity !== undefined) {
          const mq = Math.floor(Number(m.quantity))
          if (Number.isFinite(mq) && mq > MAX_MODIFIER_QUANTITY) {
            issues.push({
              code: "MODIFIER_QUANTITY_EXCEEDS_MAX",
              message: `Modifier quantity must not exceed ${MAX_MODIFIER_QUANTITY}`,
              client_item_id: id,
            })
          }
        }
      }
    }
  }
  for (const overflow of findMenuItemQuantityOverflows(lines, MAX_MENU_ITEM_QUANTITY)) {
    issues.push({
      code: "MENU_ITEM_QUANTITY_EXCEEDS_MAX",
      message: `Total quantity for menu item must not exceed ${MAX_MENU_ITEM_QUANTITY}`,
      menu_item_id: overflow.menu_item_id,
    })
  }
  return issues
}

export function assertValidCartLines(lines: CartLine[]): void {
  const issues = validateCartLines(lines)
  if (issues.length > 0) {
    throw new Error(issues.map((i) => `[${i.code}] ${i.message}`).join("; "))
  }
}

/**
 * Post-transform invariants: quantities, non-empty groups, unique modifier identities per group.
 * Call immediately before HTTP; invalid states must throw — never patch silently in the API layer.
 */
export function validateBillingPreviewCartItems(items: BillingPreviewV1CartItem[]): CartValidationIssue[] {
  const issues: CartValidationIssue[] = []
  for (const row of items) {
    const id = String(row?.client_item_id ?? "").trim()
    if (!id) {
      issues.push({ code: "PREVIEW_MISSING_CLIENT_ID", message: "Preview row missing client_item_id" })
      continue
    }
    const q = Math.floor(Number(row.quantity))
    if (!Number.isFinite(q) || q < 1) {
      issues.push({ code: "PREVIEW_INVALID_QUANTITY", message: "Preview quantity must be >= 1", client_item_id: id })
    }
    for (const g of row.selected_modifiers ?? []) {
      if (Array.isArray(g.items) && g.items.length === 0) {
        issues.push({
          code: "PREVIEW_EMPTY_MODIFIER_GROUP",
          message: "Preview row has empty modifier group",
          client_item_id: id,
        })
      }
      const seen = new Set<string>()
      for (const m of g.items ?? []) {
        const ident = resolveModifierIdentity(m)
        if (!ident.primary_key) {
          issues.push({
            code: "PREVIEW_MODIFIER_MISSING_IDENTITY",
            message: "Preview modifier missing modifier_group_item_id and child_menu_item_id",
            client_item_id: id,
          })
        } else {
          const dedupeKey = `${g.modifier_group_id}:${ident.primary_key}`
          if (seen.has(dedupeKey)) {
            issues.push({
              code: "PREVIEW_DUPLICATE_MODIFIER_IDENTITY",
              message: "Duplicate modifier identity in preview row group",
              client_item_id: id,
            })
          }
          seen.add(dedupeKey)
        }
        if (!String(ident.child_menu_item_id ?? "").trim()) {
          issues.push({
            code: "PREVIEW_MODIFIER_MISSING_CHILD",
            message: "Preview modifier missing child_menu_item_id (required for pricing API)",
            client_item_id: id,
          })
        }
        if (ident.child_menu_item_id && !ident.modifier_group_item_id) {
          issues.push({
            code: "PREVIEW_MODIFIER_MISSING_BRIDGE",
            message: "Preview modifier should include modifier_group_item_id when child_menu_item_id is set",
            client_item_id: id,
          })
        }
        const mq = Math.floor(Number(m.quantity ?? 0))
        if (!Number.isFinite(mq) || mq < 1) {
          issues.push({
            code: "PREVIEW_MODIFIER_INVALID_QUANTITY",
            message: "Preview modifier quantity must be >= 1",
            client_item_id: id,
          })
        } else if (mq > MAX_MODIFIER_QUANTITY) {
          issues.push({
            code: "PREVIEW_MODIFIER_QUANTITY_EXCEEDS_MAX",
            message: `Preview modifier quantity must not exceed ${MAX_MODIFIER_QUANTITY}`,
            client_item_id: id,
          })
        }
      }
    }
  }
  for (const overflow of findMenuItemQuantityOverflows(items, MAX_MENU_ITEM_QUANTITY)) {
    issues.push({
      code: "PREVIEW_MENU_ITEM_QUANTITY_EXCEEDS_MAX",
      message: `Total quantity for menu item must not exceed ${MAX_MENU_ITEM_QUANTITY}`,
      menu_item_id: overflow.menu_item_id,
    })
  }
  return issues
}

export function assertValidBillingPreviewCartItems(items: BillingPreviewV1CartItem[]): void {
  const issues = validateBillingPreviewCartItems(items)
  if (issues.length > 0) {
    throw new Error(issues.map((i) => `[${i.code}] ${i.message}`).join("; "))
  }
}
